import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select } from "antd";
import { ConnectionCustomFieldsTooltip } from "components/UI/ConnectionCustomFieldsTooltip/ConnectionCustomFieldsTooltip";
import { DrawerLink } from "components/UI/DrawerLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { Control, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { ResponsePropertySourceType } from "types/enums";
import { DrawerId } from "types/models";

import { FormData } from "../formSchema";

const { Option } = Select;

interface HowToUseSectionFormProps {
    name: number; // index of this field in the stepConnection array
    methodIndex: number; // index of the method in methods array
    responseHandlerIndex: number; // index of the response handler in responseHandlers array
    control: Control<FormData>;
    handleRemove: () => void;
}

export const HowToUseSectionForm: FC<HowToUseSectionFormProps> = ({
    name,
    methodIndex,
    responseHandlerIndex,
    control,
    handleRemove,
}) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor.response-how-to-use-form");
    const reqSetting = DrawerId.ResponseHowToUseSetting;

    // Base path for this field
    const propertyPath =
        `methods.${methodIndex}.requestConfig.responseHandlers.${responseHandlerIndex}.responseConnectionProperties.${name}` as const;

    // Watch fields
    const source = useWatch({
        control,
        name: `${propertyPath}.source` as const,
    }) as ResponsePropertySourceType | undefined;

    const isConstantSource = source === ResponsePropertySourceType.Constant;

    return (
        <Row gutter={16} align="middle">
            <Col span={6}>
                <FormItem control={control} name={`${propertyPath}.source`} label={t("fields.source.label")} labelCol={{ span: 24 }}>
                    <Select placeholder={t("fields.source.placeholder")}>
                        {Object.values(ResponsePropertySourceType).map((src) => (
                            <Option value={src} key={src}>
                                {src}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            {isConstantSource ? (
                <Col span={6}>
                    <FormItem
                        control={control}
                        name={`${propertyPath}.value`}
                        label={t("fields.key.label")}
                        labelCol={{ span: 24 }}
                    >
                        <Input placeholder={t("fields.key.placeholder")} />
                    </FormItem>
                </Col>
            ) : (
                <Col span={6}>
                    <FormItem
                        control={control}
                        name={`${propertyPath}.accessor`}
                        label={t("fields.key.label")}
                        labelCol={{ span: 24 }}
                    >
                        <Input placeholder={t("fields.key.placeholder")} />
                    </FormItem>
                </Col>
            )}

            <Col span={6}>
                <FormItem
                    control={control}
                    name={`${propertyPath}.system_name`}
                    label={t("fields.value.label")}
                    labelCol={{ span: 24 }}
                >
                    <Input />
                </FormItem>
            </Col>

            <ConnectionCustomFieldsTooltip />

            <Col>
                <Button danger onClick={handleRemove} icon={<DeleteOutlined />} />
            </Col>

            <Col>
                <DrawerLink
                    drawerId={reqSetting}
                    search={{
                        [`${reqSetting}-methodIndex`]: methodIndex.toString(),
                        [`${reqSetting}-responseHandlerIndex`]: responseHandlerIndex.toString(),
                        [`${reqSetting}-id`]: name.toString(),
                    }}
                    action="show"
                >
                    <Button icon={<SettingOutlined />} />
                </DrawerLink>
            </Col>
        </Row>
    );
};
